/**
 * The tab crash sentinel's last heartbeat, copied into a small cookie so that
 * Chrome's own crash report can carry it.
 *
 * WHY. Prod 2026-10-08/09: Chrome on Android reported three out-of-memory
 * crashes (/product, /sell, /account), each 3-16 s after the session started.
 * The rows said where and when and nothing else: no device memory, no heap, no
 * DOM size, so nothing could tell a phone with 1 GB from a page that leaks. The
 * sentinel measures exactly that every few seconds, but it keeps it in
 * localStorage, which the crash report never sees, and it drops a page that
 * died inside its first beat (`diedOnArrival`) -- which is when these died.
 *
 * The report upload does carry the origin's cookies (those rows have real
 * session ids, read from cookies), so the heartbeat rides along in one.
 * pages/api/errors/browser-report reads it back into the row.
 */

export const TAB_BEAT_COOKIE = 'sw_tab_beat';

/** Heartbeat fields that go into the cookie, and nothing else. */
const NUMBER_FIELDS = [
  'up',
  'dom_nodes',
  'images',
  'image_mp',
  'iframes',
  'videos',
  'js_heap_mb',
  'device_memory_gb',
  'ts',
  'added',
  'last_el_ms',
  'gam_frames',
  'nav_ms',
  'long_n',
  'long_ms',
  'long_at',
  'long_end',
  'ad_n',
  'ad_ms',
] as const;
const STRING_FIELDS = [
  'tab',
  'clean',
  'path',
  'ready_state',
  'build',
  'last_el',
  'frame_hosts',
  'long_src',
  'ad_last',
  'ad_creative',
] as const;

const MAX_STRING = 200;

export type TabBeatCookie = Partial<Record<(typeof NUMBER_FIELDS)[number] | (typeof STRING_FIELDS)[number], string>>;

/**
 * Cookie-safe value: URLSearchParams escapes everything but letters, digits
 * and `*-._`, and its separators `&` and `=` are legal in a cookie value.
 */
export function encodeTabBeat(fields: Record<string, number | string | undefined>): string {
  const params = new URLSearchParams();
  for (const key of [...STRING_FIELDS, ...NUMBER_FIELDS]) {
    const value = fields[key];
    if (value === undefined || value === '') continue;
    params.set(key, String(value).slice(0, MAX_STRING));
  }
  return params.toString();
}

/** Only the known fields come back, numbers only where a number belongs. */
export function decodeTabBeat(raw: string | undefined): TabBeatCookie | null {
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const out: TabBeatCookie = {};
  for (const key of STRING_FIELDS) {
    const value = params.get(key);
    if (value) out[key] = value.slice(0, MAX_STRING);
  }
  for (const key of NUMBER_FIELDS) {
    const value = params.get(key);
    if (value !== null && value !== '' && Number.isFinite(Number(value))) out[key] = value;
  }
  return Object.keys(out).length ? out : null;
}
